import type { ApprovalMode, EmitEvent } from "@/domains/core"
import type { EventEmitter } from "@/domains/library"
import type { Address, PublicClient } from "viem"
import type { Config } from "wagmi"
import { ClFarmStore } from "@/domains/cl-farm"
import { approveOwnerSpenderAllowance, mintGammaClFarmLiquidity } from "@/domains/on-chain"
import { erc20Abi } from "viem"

type MintClFarmLiquidityInteraction = {
  connectedAddress: Address
  chainId: number
  config: Config
  publicClient: PublicClient
  emitter: EventEmitter<EmitEvent>
  clFarm: ClFarmStore | null | undefined
  token0AmountBI: bigint
  token1AmountBI: bigint
  approvalMode: ApprovalMode
  onSuccess: () => void
}

export const mintGammaClFarmLiquidityInteraction = async ({
  connectedAddress,
  chainId,
  config,
  publicClient,
  emitter,
  clFarm,
  token0AmountBI,
  token1AmountBI,
  approvalMode,
  onSuccess,
}: MintClFarmLiquidityInteraction) => {
  if (!clFarm) {
    return emitter.emit("error-cl-farm-not-found", {
      title: "CL Farm not found",
      description: "The CL Farm you are trying to interact with does not exist.",
      variant: "error",
    })
  }

  if (!connectedAddress) {
    return emitter.emit("error-not-connected", {
      title: "Not connected",
      description: "In order to interact with this app, you first must connect your wallet.",
      variant: "error",
    })
  }

  const guardAddress = clFarm?.guardAddress?.toViemAddress()
  const vaultAddress = clFarm?.vaultAddress?.toViemAddress()
  if (!guardAddress) {
    return emitter.emit("error-cl-farm-not-found", {
      title: "CL Farm not found",
      description: "The CL Farm you are trying to interact with does not exist.",
      variant: "error",
    })
  }
  if (!vaultAddress) {
    return emitter.emit("error-invalid-cl-farm", {
      title: "Invalid CL Farm",
      description: "The CL Farm vault address is invalid. Please try again.",
      variant: "error",
    })
  }

  // STEP ONE: Check if the user has enough balance in their wallet to withdraw
  if (clFarm.token0.balance < token0AmountBI) {
    emitter.emit("error-insufficient-balance-lp", {
      title: "Insufficient balance",
      description: `You do not have enough ${clFarm.token0.symbol} in your wallet. You are trying to mint ${clFarm.token0.formatUnits(token0AmountBI - clFarm.token0.balance)} more than available.`,
      variant: "error",
    })
    return
  }
  if (clFarm.token1.balance < token1AmountBI) {
    emitter.emit("error-insufficient-balance-lp", {
      title: "Insufficient balance",
      description: `You do not have enough ${clFarm.token1.symbol} in your wallet. You are trying to mint ${clFarm.token1.formatUnits(token1AmountBI - clFarm.token1.balance)} more than available.`,
      variant: "error",
    })
    return
  }

  // STEP TWO - Does the user need to provide an allowance?
  const allowance0 = await publicClient.readContract({
    address: clFarm.token0.address.toViemAddress(),
    abi: erc20Abi,
    functionName: "allowance",
    args: [connectedAddress, vaultAddress], // THIS MUST BE THE VAULT ADDRESS
  })

  if (allowance0 < token0AmountBI) {
    emitter.emit("approve-token-allowance", {
      title: "Approving spending tokens",
      description: `Seeking approval of ${clFarm.token0.formatUnits(token0AmountBI)} ${clFarm.token0.symbol}`,
      variant: "info",
    })

    const approval = await approveOwnerSpenderAllowance({
      account: connectedAddress,
      chainId,
      config,
      contractAddress: clFarm.token0.address.toViemAddress(),
      spenderAddress: vaultAddress, // THIS MUST BE THE VAULT ADDRESS
      spenderAmount: token0AmountBI,
      approvalMode,
      contractRiskLevel: "immutable",
    })

    if (approval.success) {
      emitter.emit("approve-token-allowance", {
        title: "Approval successful",
        description: "You may now proceed to mint",
        variant: "success",
      })
    }

    if (approval.success === false) {
      emitter.emit("approve-token-allowance", {
        title: "Unable to approve spending tokens",
        description: approval.error ?? "unknown error",
        variant: "error",
      })
      return
    }
  }

  const allowance1 = await publicClient.readContract({
    address: clFarm.token1.address.toViemAddress(),
    abi: erc20Abi,
    functionName: "allowance",
    args: [connectedAddress, vaultAddress], // THIS MUST BE THE VAULT ADDRESS
  })

  if (allowance1 < token1AmountBI) {
    emitter.emit("approve-token-allowance", {
      title: "Approving spending tokens",
      description: `Seeking approval of ${clFarm.token1.formatUnits(token1AmountBI)} ${clFarm.token1.symbol}`,
      variant: "info",
    })

    const approval = await approveOwnerSpenderAllowance({
      account: connectedAddress,
      chainId,
      config,
      contractAddress: clFarm.token1.address.toViemAddress(),
      spenderAddress: vaultAddress, // THIS MUST BE THE VAULT ADDRESS
      spenderAmount: token1AmountBI,
      approvalMode,
      contractRiskLevel: "immutable",
    })

    if (approval.success) {
      emitter.emit("approve-token-allowance", {
        title: "Approval successful",
        description: "You may now proceed to mint",
        variant: "success",
      })
    }

    if (approval.success === false) {
      emitter.emit("approve-token-allowance", {
        title: "Unable to approve spending tokens",
        description: approval.error ?? "unknown error",
        variant: "error",
      })
      return
    }
  }

  // STEP THREE: process the transaction
  const mintClFarmLiquidityResult = await mintGammaClFarmLiquidity({
    account: connectedAddress,
    chainId,
    config,
    guardAddress,
    vaultAddress,
    token0AmountBI,
    token1AmountBI,
  })

  if (mintClFarmLiquidityResult.success) {
    onSuccess?.()
    emitter.emit("success-mint-cl-farm-liquidity", {
      title: `Mint ${clFarm.displayName}`,
      description: `Minted ${clFarm.displayName} tokens with ${clFarm.token0.symbol} and ${clFarm.token1.symbol}`,
      variant: "success",
    })
  } else {
    emitter.emit("error-mint-cl-farm-liquidity", {
      title: `Unable to mint ${clFarm.displayName}`,
      description: mintClFarmLiquidityResult.error || "Unknown error",
      variant: "error",
    })
  }

  return mintClFarmLiquidityResult
}
